/**
 * Общие элементы интерфейса — §13.2: кнопки, формы, таблицы, значки,
 * модальное окно, обёртка для графиков.
 *
 * Компонентов ровно столько, сколько нужно единственному экрану платформы
 * (§13.3) и типовому экрану сервиса. Набор «на будущее» здесь не заводится:
 * компонент, который никто не использует, невозможно проверить, и он
 * устаревает раньше, чем понадобится.
 */

import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useEffect } from 'react';

/* ── Кнопка ────────────────────────────────────────────────────────── */

type ButtonVariant = 'default' | 'primary' | 'danger' | 'quiet';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  small?: boolean;
}

export function Button({ variant = 'default', small, className, ...rest }: ButtonProps) {
  const classes = ['button'];
  if (variant !== 'default') classes.push(`button--${variant}`);
  if (small) classes.push('button--small');
  if (className) classes.push(className);
  return <button type="button" className={classes.join(' ')} {...rest} />;
}

/* ── Поля ──────────────────────────────────────────────────────────── */

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="select" {...props} />;
}

/* ── Карточка ──────────────────────────────────────────────────────── */

export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card__header">
          {title && <h2 className="card__title">{title}</h2>}
          <span className="card__spacer" />
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

/* ── Значок уровня доступа ─────────────────────────────────────────── */

export function LevelBadge({ level }: { level: string | null | undefined }) {
  if (!level) {
    return (
      <span className="badge badge--none" aria-label="доступа нет">
        —
      </span>
    );
  }
  return <span className={`badge badge--${level}`}>{level}</span>;
}

/* ── Модальное окно ────────────────────────────────────────────────── */

export function Modal({
  title,
  onClose,
  children,
  actions,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
}) {
  // Escape закрывает окно: на телефоне это клавиша «назад» аппаратной
  // клавиатуры, на компьютере — привычка.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          {/* Escape и нажатие мимо окна закрывают его тоже, но на телефоне
              ни того, ни другого человек не знает: видимая кнопка —
              единственный способ, который не надо угадывать. */}
          <Button small variant="quiet" onClick={onClose}>
            Закрыть
          </Button>
        </div>
        {children}
        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

/* ── Обёртка для графиков ──────────────────────────────────────────── */

/**
 * §13.2: «Общий компонент-обёртка; выбор библиотеки — O10 финансового ТЗ».
 *
 * Обёртка держит рамку, заголовок, отступы и поведение при отсутствии
 * данных — то, что должно быть одинаковым во всех трёх сервисах. Саму
 * отрисовку графика делает то, что окажется внутри: библиотека ещё не
 * выбрана (вопрос O10 финансового ТЗ), и предрешать её здесь нельзя.
 */
export function ChartFrame({
  title,
  isEmpty,
  emptyText = 'Данных пока нет',
  children,
}: {
  title?: string;
  isEmpty?: boolean;
  emptyText?: string;
  children: ReactNode;
}) {
  return (
    <figure className="chart-frame" style={{ margin: 0 }}>
      {title && <figcaption className="chart-frame__title">{title}</figcaption>}
      <div className="chart-frame__body">
        {isEmpty ? <p className="faint">{emptyText}</p> : children}
      </div>
    </figure>
  );
}
